import {Component, Inject, OnInit, Optional} from '@angular/core';
import {combineLatest, EMPTY, Observable, of, Subject, Subscription} from 'rxjs';
import * as EmployeeModels from '@employee-models/index';
import {BadgeRequestsService} from '../../core/services/data-access/badge-requests.service';
import {StepperControlsClass} from '@ui-app/components/stepper/stepper.controls.class';
import {IBadgeRequestPhotoInfo, ICroppPhotoRequest, IPPMDSearchInMyLocationResult, OnBehalfRequestorDetails} from '@employee-models/data.models';
import {PhotoData, UPLOAD_URL, UPLOAD_VISITOR_URL} from '@shared-models/common.models';
import {catchError, filter, map, switchMap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmModalComponent} from '@ui-app/modals/confirm-modal/confirm-modal.component';
import {RoutesConfig} from '@employee-configs/routes.config';
import {EmployeeBusinessService} from '../../core/services/business.service';
import {S3DeleteService} from '@ui-app/services/s3-delete.service';
import {FileUploadService} from '@ui-app/components/file-upload/file-upload.service';
import {PhotoReviewService} from '../../core/services/data-access/photo-review.service';
import {AutomaticCroppingService, IAutoCropResultData} from '@ui-app/services/automatic-cropping.service';
import {IAllCropperData} from '../../modals/photo-review-modal/cropper.models';
import {EReturnImageResizeType} from '@shared-models/common.enums';
import {ImageService} from '@ui-app/services/image.service';

@Component({
    selector: 'employee-badge-requests-page',
    templateUrl: './employee-badge-request-page.component.html',
    styleUrls: ['./employee-badge-request-page.component.less'],
})
export class EmployeeBadgeRequestPageComponent extends StepperControlsClass implements OnInit {
    flowType = EmployeeModels.CE.BadgeRequestFlow.Default;
    requestReasonPayload: EmployeeModels.CM.IBadgeRequestPayloadModel = {
        reason: null,
        category: null,
        deliverySiteId: null,
        deliverySiteName: '',
        files: [],
        isChanges: false,
        isPreferredFullName: false,
        bloodGroupId: null,
    };
    requestData: EmployeeModels.DM.IBadgeRequestInfoEmployee;
    canUseCurrentImage: boolean = false;
    isCurrentPhotoUsed: boolean = false;
    canOpenTakePhotoStepper: boolean = false;
    isShowSubmitBtn: boolean = false;
    userPhotoUrl: string = null;
    croppedPhotoBase64: string = null;
    croppedPhotoSmallBase64: string = null;
    userPhotoData: PhotoData = null;
    userData: EmployeeModels.DM.IUserData;
    newFileName: string;
    cropperData: IAllCropperData;
    isImageRecognitionEnabled: boolean;
    automaticallyCropTheImage$: Subject<void> = new Subject<void>();
    isCanSubmit: boolean = true;
    imageResize$: Subject<void> = new Subject();
    isPPMD = false;
    isOnBehalf = false;
    isUserCanTakePhoto = true;
    user: IPPMDSearchInMyLocationResult;
    onBehalfRequestor: OnBehalfRequestorDetails;

    constructor(
        public badgeRequestsService: BadgeRequestsService,
        public photoReviewService: PhotoReviewService,
        public router: Router,
        public employeeBusinessService: EmployeeBusinessService,
        public deleteFileService: S3DeleteService,
        public automaticCroppingService: AutomaticCroppingService,
        public imageService: ImageService,
        @Optional() @Inject(UPLOAD_URL) public uploadUrl: string,
        @Optional() @Inject(UPLOAD_VISITOR_URL) public uploadVisitorUrl: string,
        @Optional() @Inject(FileUploadService) public fileUploadService: FileUploadService,
        protected route: ActivatedRoute
    ) {
        super();
    }

    get isVisitorRequest(): boolean {
        return this.badgeRequestsService.getIsVisitorRequest(this.requestReasonPayload?.category)
    }

    ngOnInit(): void {
        this.subs = this.getRequestDetailAction();
        this.subs = this.getUserDataAction();

        this.subs = this.automaticCroppingService.getSetting().subscribe((settings) => {
            this.isImageRecognitionEnabled = settings.isImageRecognitionEnabled;
        });

        this.route.queryParams.subscribe((params) => {
            this.isPPMD = params['isPPMD'] === 'true' ? true : false;
            this.isOnBehalf = params['isOnBehalf'] === 'true' ? true : false;
            this.isUserCanTakePhoto = !this.isPPMD && !this.isOnBehalf ;
        });
        this.subs = this.automaticallyCropTheImage$
            .pipe(
                filter(() => this.isImageRecognitionEnabled),
                switchMap(() => {
                    this.isCanSubmit = false;
                    return this.automaticCroppingService.automaticallyCropTheImage(
                        this.userPhotoData.uId,
                        250,
                        375,
                        this.isVisitorRequest
                    ).pipe(
                        catchError(() => {
                            this.deletePhotoData();
                            this.isCanSubmit = false;
                            return of(null);
                        }),
                        filter((x) => !!x)
                    );
                })
            )
            .subscribe((croppedData: IAutoCropResultData) => {
                this.croppedPhotoBase64 = croppedData.croppedImageBase64;
                this.cropperData = croppedData.cropperData;
                this.isImageRecognitionEnabled = croppedData.isImageRecognitionEnabled;
                this.imageResize$.next();
            });

        this.subs = this.imageResize$
            .pipe(
                switchMap(() => {
                    return this.imageService.changeImageSize(
                        this.imageService.fromDataUrlToFile(this.croppedPhotoBase64, 'tmpFilename'),
                        this.imageService.imageDefaultType,
                        600,
                        EReturnImageResizeType.dataUrl,
                        'tmpFilename'
                    );
                })
            )
            .subscribe((smallBase64: string) => {
                this.croppedPhotoSmallBase64 = smallBase64;
                this.isCanSubmit = true;
            });
    }
    back(){
        if(this.isOnBehalf || this.isPPMD){
            this.canOpenTakePhotoStepper = false;
        }
        super.back();
    }
    getRequestDetailAction(): Subscription {
        let headers = [];
        if ((this.isPPMD || this.isOnBehalf) && this.user) {
            headers = [
                {
                    key: 'ActAs',
                    value: this.user.alias,
                },
            ];
            this.onBehalfRequestor = {
                firstName: this.user.firstName,
                email: this.user.email,
                lastName: this.user.lastName,
                siteName: this.user.siteName,
                function: '',
                userId: this.user.userId,
                phoneNumber: '',
            }
        }
        return this.badgeRequestsService
            .getBadgeRequestInfo(headers)
            .pipe(
                map((data) => {
                    if (!data.siteName) {
                        data.siteName = '';
                    }
                    return data;
                })
            )
            .subscribe((result) => {
                this.requestReasonPayload.deliverySiteId = result.siteId;
                this.requestReasonPayload.deliverySiteName = this.findSiteById(result.sites, result.siteId);
                this.requestReasonPayload.bloodGroupId = result.isBloodGroupRequired ? result.bloodGroupId : null;
                this.requestData = result;
                this.canUseCurrentImage = result.canUseCurrentImage;

                /**
                 * [2397570] [Employee] Badge request - Resubmit photo
                 */
                if(this.isPPMD && this.user?.isPrehire) {
                    this.requestData.badgeRequestType = EmployeeModels.DE.BadgeRequestTypeEnum.MissingPhoto;
                }
                if (
                    result.badgeRequestType === EmployeeModels.DE.BadgeRequestTypeEnum.Resubmission &&
                    result.lastBadgeRequestReason in EmployeeModels.DE.BadgeReasonEnum
                ) {
                    this.flowType = EmployeeModels.CE.BadgeRequestFlow.Resubmission;

                    this.requestReasonPayload.reason = result.lastBadgeRequestReason;
                    this.requestReasonPayload.deliverySiteId = result.deliverySiteId;
                    this.requestReasonPayload.deliverySiteName = this.findSiteById(result.sites, result.deliverySiteId);
                    this.requestReasonPayload.files = [];
                    this.requestReasonPayload.isChanges = false;
                    this.canUseCurrentImage = false;

                    this.requestData = {
                        ...this.requestData,
                        canUseCurrentImage: false,
                    };
                    this.checkValidNextStep(this.requestReasonPayload);
                }
                else if ((this.isPPMD || this.isOnBehalf) && result && this.user) {
                    this.canOpenTakePhotoStepper = result.canUploadBadgeRequest;
                    this.onBehalfRequestor.function = result.functionName;
                    this.onBehalfRequestor.phoneNumber = result.userPhoneNumber;
                }                
                    
            });
    }

    getUserDataAction(): Subscription {
        return this.employeeBusinessService.userProfileService.userData$.subscribe((result) => {
            this.userData = result;
            this.newFileName = `${this.userData.lastName}, ${this.userData.firstName}`;
        });
    }

    pPMDUserSelected(event: IPPMDSearchInMyLocationResult) {
        this.user = event;
        if (this.user) {
            this.getRequestDetailAction();
            this.newFileName = `${this.user.lastName}, ${this.user.firstName}`
        }
        this.canOpenTakePhotoStepper = false;
    }

    checkValidNextStep(event: EmployeeModels.CM.IBadgeRequestPayloadModel): void {
        if ((!!event.bloodGroupId || !this.requestData.isBloodGroupRequired) && !!event.deliverySiteId && !!event.reason) {
            this.requestReasonPayload = event;
            this.requestReasonPayload.isChanges = event.isChanges;
            this.requestData.siteId = event.deliverySiteId;
            this.requestData.bloodGroupId = event.bloodGroupId;
            this.canOpenTakePhotoStepper = true;
            if ((this.isPPMD || this.isOnBehalf) && this.requestData && this.user) {
                this.canOpenTakePhotoStepper = this.requestData.canUploadBadgeRequest;
            }
        }
    }

    useCurrentPhotoAction(file: IBadgeRequestPhotoInfo): void {
        this.isCurrentPhotoUsed = true;
        this.uploadAction(file);
    }

    uploadAction(file: IBadgeRequestPhotoInfo): void {
        this.requestReasonPayload.isChanges = true;
        this.userPhotoUrl = file.url;
        this.userPhotoData = { uId: file.uId };
        this.requestReasonPayload.files = [file];
        this.isShowSubmitBtn = true;
        this.automaticallyCropTheImage$.next();
    }

    deleteAction(): void {
        this.deletePhotoData();
    }

    deletePhotoData(): void {
        this.deleteSubmitedFile();
        this.userPhotoData = null;
        this.userPhotoUrl = '';
        this.isCurrentPhotoUsed = false;
        this.requestReasonPayload.files = [];
        this.isShowSubmitBtn = false;
        this.croppedPhotoSmallBase64 = null;
        this.croppedPhotoBase64 = null;
        this.cropperData = null;
        this.isCanSubmit = false;
    }

    openTakePhotoStepper(): void {
        this.canUseCurrentImage = this.requestData.canUseCurrentImage;
        this.next();
    }

    submit(): void {
        if (!this.isCanSubmit) {
            return;
        }

        const { reason, category, deliverySiteId, files, isPreferredFullName, bloodGroupId } = this.requestReasonPayload;
        const payload = { reason, category, deliverySiteId, files, isPreferredFullName, bloodGroupId };
        const {
            canvasData: { naturalWidth, naturalHeight },
        } = this.cropperData;
        payload.files[0] = { ...payload.files[0], height: naturalHeight, width: naturalWidth };

        const request: ICroppPhotoRequest = {
            badgeRequestId: null,
            croppInfo: JSON.stringify(this.cropperData),
            isAutocropped: true,
            uId: null,
        };

        this.subs = this.getSaveUploadStream(payload, request).subscribe(
            () => {
                this.employeeBusinessService.toasterService.success('Badge request successfully submitted');
                this.openEmployeeProfile(this.userData.userId);
            },
            () => {
                this.employeeBusinessService.toasterService.error(
                    `The badge request wasn't created. Please refresh the page and try again`
                );
            }
        );
    }

    checkRequestChanges(): void {
        this.requestReasonPayload.isChanges ? this.openConfirmModal() : this.openEmployeeProfile(this.userData.userId);
    }

    openConfirmModal(): void {
        this.subs = this.employeeBusinessService.customModalService
            .initModal(ConfirmModalComponent, {})
            .pipe(
                filter((action: boolean) => action),
                switchMap(() => {
                    this.openEmployeeProfile(this.userData.userId);
                    return EMPTY;
                })
            )
            .subscribe(() => {
                this.employeeBusinessService.customModalService.closeAllModals();
            });
    }

    openEmployeeProfile(userId: number): void {
        this.router.navigate([RoutesConfig.pages.employeeProfile, userId]);
    }

    deleteSubmitedFile(): void {
        if (this.userPhotoData && this.userPhotoData.uId && !this.isCurrentPhotoUsed) {
            this.subs = this.deleteFileService.deleteFile(this.userPhotoData.uId).subscribe();
        }
    }

    getSaveUploadStream(payload: any, request: ICroppPhotoRequest): Observable<any> {
        let headers = [];
        if (this.isPPMD || this.isOnBehalf) {
            headers = [
                {
                    key: 'ActAs',
                    value: this.user.alias,
                },
            ];
        }
        if (this.isImageRecognitionEnabled) {
            const url = this.isVisitorRequest ? this.uploadVisitorUrl : this.uploadUrl;
            const uploadResizedPhoto$ = this.fileUploadService.uploadFile(
                this.imageService.fromDataUrlToFile(this.croppedPhotoSmallBase64, 'tmpFilename', this.imageService.imageDefaultType),
                url
            );

            return combineLatest([this.badgeRequestsService.saveUpload(payload, headers), uploadResizedPhoto$]).pipe(
                switchMap(([badgeRequestId, photoData]) => {
                    return this.automaticCroppingService.cropPhoto({ ...request, badgeRequestId, uId: photoData.uId });
                })
            );
        }
        return this.badgeRequestsService.saveUpload(payload, headers);
    }

    protected findSiteById(sites: EmployeeModels.DM.ISiteDto[], id: number): string {
        const site = sites.find(({ siteId }) => siteId === id);
        return site ? site.siteName : '';
    }
}
